import { useState, useEffect } from 'react'
import { Button, Tabs, Spin } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { getStockFinancedetail } from '@/api/stock'
import type { StockFinancialData } from '@/types/stockPool'
import { mockStocks } from '../mockData'
import Overview from './components/Overview'
import Financials from './components/Financials'
import KChart from './components/KChart'
import Reports from './components/Reports'
import QuarterlyFinancials from './components/QuarterlyFinancials'
import type { StockDetail } from '../types'
import './index.scss'

const tabItems = [
    { label: '概览', key: '1' },
    { label: '财务数据', key: '2' },
    { label: 'K线', key: '3' },
    { label: '研报', key: '4' },
    { label: '季度财务数据', key: '5' },
]

const marketLabel: Record<string, string> = { hk: '港股', us: '美股', a: 'A股', other: '其他' }

function deriveMarket(code: string) {
    const prefix = code.split('.')[0].toUpperCase()
    return ({ HK: 'hk', US: 'us', SH: 'a', SZ: 'a' } as Record<string, string>)[prefix] ?? 'other'
}

export default function StockKBDetailPage() {
    const navigate = useNavigate()
    const { code } = useParams<{ code: string }>()
    const [activeKey, setActiveKey] = useState('1')
    const [latestInd, setLatestInd] = useState<StockFinancialData | null>(null)
    const [stockName, setStockName] = useState<string>('')
    const [loading, setLoading] = useState(true)
    const [fallback, setFallback] = useState<StockDetail | null>(null)

    const decodedCode = decodeURIComponent(code ?? '')

    useEffect(() => {
        if (!code) return
        setLoading(true)
        // 尝试从后端获取最新一期财报作为概览数据
        getStockFinancedetail(decodedCode)
            .then(res => {
                if (res.items.length > 0) {
                    setLatestInd(res.items[0])
                    setStockName(res.name ?? decodedCode)
                } else {
                    // 后端无数据，使用 mock
                    const mock = mockStocks.find(s => s.code === decodedCode)
                    if (mock) { setFallback(mock); setStockName(mock.name) }
                }
            })
            .catch(() => {
                const mock = mockStocks.find(s => s.code === decodedCode)
                if (mock) { setFallback(mock); setStockName(mock.name) }
            })
            .finally(() => setLoading(false))
    }, [code])

    const market = deriveMarket(decodedCode)
    const displayPrice = latestInd?.closing_price ?? fallback?.indicators?.[0]?.closing_price
    const displayCurrency = latestInd?.currency ?? fallback?.indicators?.[0]?.currency ?? 'HKD'
    const priceSymbol = displayCurrency === 'HKD' ? 'HK$' : displayCurrency === 'USD' ? '$' : '¥'

    if (loading) {
        return (
            <div className="skd" style={{ justifyContent: 'center', alignItems: 'center' }}>
                <Spin size="large" />
            </div>
        )
    }

    if (!latestInd && !fallback) {
        return (
            <div className="skd">
                <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)' }}>
                    股票 {decodedCode} 暂无数据
                    <br />
                    <Button style={{ marginTop: 16 }} onClick={() => navigate('/stock-kb')}>返回列表</Button>
                </div>
            </div>
        )
    }

    // 构造传给 Overview 的数据
    const overviewStock: StockDetail = fallback ?? {
        code: decodedCode,
        name: stockName,
        sector: '',
        market: market as any,
        currency: displayCurrency,
        listing_date: '',
        total_shares: 0,
        indicators: latestInd ? [latestInd as any] : [],
        klines: [],
        reports: [],
    }

    return (
        <div className="skd">
            <div className="page-header flex flex-between skd__header">
                <div className="skd__title-group">
                    <Button size="small" type="text" className="skd__back" icon={<ArrowLeftOutlined />} onClick={() => navigate('/stock-kb')}>
                        个股知识库
                    </Button>
                    /
                    <span className="skd__name">{stockName || decodedCode}</span>
                    <span className="skd__code">{decodedCode}</span>
                    <span className={`skd__market-tag skd__market-tag--${market}`}>{marketLabel[market]}</span>
                </div>
                <div className="skd__header-right">
                    <span className="skd__price-label">
                        报告期收盘{latestInd?.report_date ? `（截至 ${latestInd.report_date}）` : ''}
                    </span>
                    <span className="skd__price">
                        {displayPrice ? `${priceSymbol}${displayPrice.toFixed(2)}` : '—'}
                    </span>
                </div>
            </div>

            <div className="page-content skd__content">
                <Tabs activeKey={activeKey} items={tabItems} onChange={setActiveKey} />
                <div className="skd__tab-body">
                    {activeKey === '1' && <Overview stock={overviewStock} />}
                    {activeKey === '2' && <Financials fallbackIndicators={fallback?.indicators} />}
                    {activeKey === '3' && <KChart fallbackKlines={fallback?.klines} name={stockName || decodedCode} />}
                    {activeKey === '4' && <Reports fallbackReports={fallback?.reports} />}
                    {activeKey === '5' && <QuarterlyFinancials />}
                </div>
            </div>
        </div>
    )
}
